import { FC, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { Userbot } from "../../interfaces"
import {
  useRoleQuery,
  useLazyClientsQuery,
  useAddRoleClientsMutation,
  useRemoveRoleClientsMutation,
} from "../../store"
import { useInfiniteSelectList } from "@/core/hooks"
import { infiniteListboxProps } from "@/core/components"
import { useUserbotWriteAccess } from "../../hooks"
import { getApiErrorMessage } from "@/core/utils"

interface IProps {
  roleId: string
}

export const RoleClientsManager: FC<IProps> = ({ roleId }) => {
  const canWrite = useUserbotWriteAccess()
  const { data: role, isFetching } = useRoleQuery({ id: roleId })
  const [lazyClients] = useLazyClientsQuery()
  const clientsList = useInfiniteSelectList<Userbot.Client>({
    fetchPage: (p) => lazyClients(p).unwrap(),
    getId: (c) => c.id,
  })
  const [addClients, addState] = useAddRoleClientsMutation()
  const [removeClients, removeState] = useRemoveRoleClientsMutation()

  const [toAdd, setToAdd] = useState<Userbot.Client[]>([])
  const [error, setError] = useState<string | null>(null)

  const memberIds = useMemo(
    () => new Set((role?.clients ?? []).map((c) => c.id)),
    [role],
  )
  // Компромисс §5.2: уже добавленные члены могут попадаться в результатах
  // поиска (фильтр только по загруженным страницам).
  const candidates = useMemo(
    () => clientsList.items.filter((c) => !memberIds.has(c.id)),
    [clientsList.items, memberIds],
  )

  const handleAdd = async () => {
    setError(null)
    if (!toAdd.length) return
    try {
      await addClients({
        id: roleId,
        body: { clientIds: toAdd.map((c) => c.id) },
      }).unwrap()
      setToAdd([])
    } catch (e) {
      setError(getApiErrorMessage(e))
    }
  }

  const handleRemove = async (clientId: string) => {
    setError(null)
    try {
      await removeClients({
        id: roleId,
        body: { clientIds: [clientId] },
      }).unwrap()
    } catch (e) {
      setError(getApiErrorMessage(e))
    }
  }

  if (isFetching && !role) return <CircularProgress />

  return (
    <Stack spacing={2} mt={1}>
      <Typography variant="subtitle1">Состав роли</Typography>
      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        {(role?.clients ?? []).length === 0 && (
          <Typography color="text.secondary" variant="body2">
            Нет клиентов в пуле.
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(role?.clients ?? []).map((c) => (
            <Chip
              key={c.id}
              label={c.name}
              onDelete={canWrite ? () => handleRemove(c.id) : undefined}
              disabled={removeState.isLoading}
            />
          ))}
        </Stack>
      </Box>

      {canWrite && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Autocomplete<Userbot.Client, true>
            multiple
            sx={{ flex: 1 }}
            options={candidates}
            value={toAdd}
            onChange={(_, v) => setToAdd(v)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(o) => o}
            onInputChange={(_, v, reason) => {
              if (reason === "input") clientsList.setSearch(v)
            }}
            renderInput={(params) => (
              <TextField {...params} label="Добавить клиентов" />
            )}
            {...infiniteListboxProps({
              hasMore: clientsList.hasMore,
              isFetching: clientsList.isFetching,
              onLoadMore: clientsList.loadMore,
            })}
            renderOption={(props, o, state) =>
              [props, o.name, state] as React.ReactNode
            }
          />
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!toAdd.length || addState.isLoading}
            sx={{ mt: 1 }}
          >
            Добавить
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
